// Aignoz Founder Copilot — Phase 5
// All sections derive from existing run data. Zero new LLM calls. Zero fabricated ecosystem data.
import { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingUp, AlertTriangle, Lock, RefreshCw, ChevronRight, ChevronDown,
  Sparkles, ListChecks, Coins, Gauge, Microscope, Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { usePersona, PersonaIcon } from "@/persona";
import { formatINR } from "@/currency";
import { computeTRL, PERSONAS } from "@/personas";

// ============================================================================
// Helpers — deterministic computations from existing doc
// ============================================================================

const SEVERITY_RANK = { critical: 4, high: 3, medium: 2, low: 1 };
const SEVERITY_SCORE = { Low: 10, Medium: 30, High: 60, Critical: 85 };

export function severityKey(s) {
  if (!s) return "Medium";
  const norm = String(s).trim();
  const cap = norm[0].toUpperCase() + norm.slice(1).toLowerCase();
  return ["Low", "Medium", "High", "Critical"].includes(cap) ? cap : "Medium";
}

export function computeFundingReadiness(doc) {
  const sc = doc?.scores || {};
  const fr = doc?.founder_review || {};
  const investor = sc.investor_attractiveness_score?.score || 0;
  const feasibility = sc.feasibility_score?.score || 0;
  const commSev = severityKey(fr.commercialization_risk?.severity);
  const commercialReadiness = 100 - (SEVERITY_SCORE[commSev] || 30);
  return Math.round((investor + feasibility + commercialReadiness) / 3);
}

export function computeFundingRequired(doc) {
  const outs = doc?.outputs || {};
  const pd = outs.prototype_designer || {};
  const ct = outs.clinical_trial_designer || {};
  const bom = pd.bill_of_materials || pd.bom || [];

  let bomTotal = 0;
  for (const i of bom) {
    const cost = Number(i.est_cost_usd ?? i.cost_usd ?? i.cost ?? 0);
    const rawQty = i.qty ?? i.quantity ?? 1;
    // Lenient qty parse — handles "1 set", "2 pcs", numbers, and strings.
    const m = String(rawQty).match(/^\s*(\d+(?:\.\d+)?)/);
    const qty = m ? Number(m[1]) : 1;
    if (!Number.isNaN(cost)) bomTotal += cost * qty;
  }
  const pilotHardware = bomTotal * 100;     // 100-unit pilot scale
  const validation = 200000;
  const sampleSize = Number(ct.sample_size_estimate || ct.target_enrollment || 200);
  const months = Number(ct.timeline_months || ct.duration_months || 12);
  const trial = sampleSize * 7000 + months * 30000;
  const regulatory = 150000;
  const raw = pilotHardware + validation + trial + regulatory;
  const rounded = Math.round(raw / 100000) * 100000;  // nearest $100k
  return {
    total: rounded,
    breakdown: {
      pilotHardware: Math.round(pilotHardware),
      validation,
      trial: Math.round(trial),
      regulatory,
    },
    raw,
    bomTotal,
    sampleSize,
    months,
  };
}

export function computeTRLBlockers(doc) {
  const outs = doc?.outputs || {};
  const blockers = [];

  const vp = outs.validation_planner || {};
  const successCriteria = vp.success_criteria || [];
  if (successCriteria.length) {
    blockers.push({
      kind: "Validation",
      text: String(successCriteria[0]).slice(0, 120),
    });
  }

  const rp = outs.regulatory_planner || {};
  const docs = rp.required_documentation || [];
  if (docs.length) {
    // Prefer ISO/IEC-flagged doc as strategic anchor
    const strategic = docs.find((d) => /ISO|IEC|FDA|CDSCO|CE|MDR|IVDR|13485/i.test(String(d))) || docs[0];
    blockers.push({
      kind: "Regulatory",
      text: String(strategic).slice(0, 120),
    });
  }

  const ca = outs.commercialization_agent || {};
  const pilots = ca.pilot_partners || ca.launch_partners || [];
  if (pilots.length) {
    const names = pilots.slice(0, 2).map((p) => {
      const n = typeof p === "string" ? p : (p.name || "");
      return n.split(/[,(]/)[0].trim().slice(0, 38);
    });
    blockers.push({
      kind: "Commercialization",
      text: `Secure pilot LOIs with ${names.join(" + ")}`,
    });
  }

  return blockers.slice(0, 3);
}

export function topRisks(doc, n = 3, allowKinds = null) {
  const fr = doc?.founder_review || {};
  const all = [
    { kind: "Technical", risk: fr.technical_risk },
    { kind: "Clinical", risk: fr.clinical_risk },
    { kind: "Regulatory", risk: fr.regulatory_risk },
    { kind: "Commercialization", risk: fr.commercialization_risk },
    { kind: "Scientific", risk: fr.scientific_risk },
  ].filter((r) => r.risk);

  const filtered = allowKinds ? all.filter((r) => allowKinds.includes(r.kind)) : all;
  return filtered
    .map((r) => ({
      kind: r.kind,
      severity: severityKey(r.risk.severity),
      explanation: r.risk.explanation || r.risk.description || "",
    }))
    .sort((a, b) => (SEVERITY_RANK[b.severity.toLowerCase()] || 0) - (SEVERITY_RANK[a.severity.toLowerCase()] || 0))
    .slice(0, n);
}

const fmtUSD = (n) => {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${n}`;
};

const scoreColor = (n) =>
  n >= 70 ? "text-emerald-300" :
  n >= 50 ? "text-amber-300" :
  "text-rose-300";

const sevColor = (k) => ({
  Critical: "bg-rose-500/15 text-rose-200 border-rose-400/40",
  High: "bg-amber-500/15 text-amber-200 border-amber-400/40",
  Medium: "bg-sky-500/15 text-sky-200 border-sky-400/40",
  Low: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
}[k] || "bg-white/10 text-white/70 border-white/20");

// ============================================================================
// Persona section ordering & framing
// ============================================================================
const SECTION_CONFIG = {
  founder: {
    title: "FOUNDER COPILOT",
    tagline: "What to do next",
    order: ["trl", "actions", "blockers", "readiness", "funding", "risks", "coming"],
    sectionLabels: {},
  },
  investor: {
    title: "INVESTOR COPILOT",
    tagline: "Diligence at a glance",
    order: ["readiness", "funding", "risks", "trl", "actions", "blockers", "coming"],
    sectionLabels: {
      readiness: "Investor Readiness Score",
      actions: "What founders need to do",
    },
  },
  researcher: {
    title: "RESEARCHER COPILOT",
    tagline: "Scientific gaps & validation pathway",
    order: ["blockers", "trl", "actions", "risks", "readiness", "coming"],
    sectionLabels: {
      blockers: "Scientific & validation gaps",
      trl: "Technology readiness",
      readiness: "Funding signal (preview)",
    },
    risksFilter: ["Scientific", "Technical"],
    readinessCompact: true,
  },
  clinician: {
    title: "CLINICIAN COPILOT",
    tagline: "Adoption & validation readiness",
    order: ["blockers", "risks", "trl", "actions", "coming"],
    sectionLabels: {
      blockers: "Validation & regulatory gaps",
    },
    risksFilter: ["Clinical", "Regulatory"],
  },
  incubator: {
    title: "INCUBATOR COPILOT",
    tagline: "Maturity & infrastructure assessment",
    order: ["trl", "blockers", "funding", "risks", "actions", "coming"],
    sectionLabels: { blockers: "Maturity blockers" },
  },
  student: {
    title: "AIGNOZ COPILOT",
    tagline: "Learn how this venture progresses",
    order: ["trl", "actions", "blockers", "readiness", "funding", "risks", "coming"],
    sectionLabels: {},
    studentTip: true,
  },
  enterprise: {
    title: "AIGNOZ COPILOT",
    tagline: "Strategic snapshot",
    order: ["trl", "actions", "blockers", "readiness", "funding", "risks", "coming"],
    sectionLabels: {},
  },
  guest: {
    title: "AIGNOZ COPILOT",
    tagline: "What to do next",
    order: ["trl", "actions", "blockers", "readiness", "funding", "risks", "coming"],
    sectionLabels: {},
  },
};

// ============================================================================
// Sub-sections
// ============================================================================

function SectionWrap({ kicker, children, testId }) {
  return (
    <section className="px-4 py-3 border-b border-white/[0.04]" data-testid={testId}>
      {kicker && (
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">{kicker}</div>
      )}
      {children}
    </section>
  );
}

function TRLSection({ doc, labelOverride }) {
  const { trl, milestone } = useMemo(() => computeTRL(doc), [doc]);
  const pct = (trl / 9) * 100;
  return (
    <SectionWrap kicker={labelOverride || "Technology Readiness Level"} testId="copilot-trl">
      <div className="flex items-baseline gap-2">
        <div className="text-3xl font-semibold text-teal-200 mono leading-none">{trl}</div>
        <div className="text-xs text-white/40 mono">/ 9</div>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="ml-auto text-[10px] text-white/40 cursor-help underline decoration-dotted">scale 1-9</div>
            </TooltipTrigger>
            <TooltipContent className="text-xs max-w-[240px]">
              Technology Readiness Level — DOD/NASA scale 1-9. Higher = closer to market.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="mt-2 h-1 rounded-full bg-white/[0.06] overflow-hidden">
        <div className="h-full bg-gradient-to-r from-teal-400 to-emerald-400" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2.5 text-[10px] uppercase tracking-[0.14em] text-white/35">Next milestone</div>
      <div className="text-xs text-white/85 leading-snug mt-0.5">{milestone}</div>
    </SectionWrap>
  );
}

function ActionsSection({ doc, runId, labelOverride }) {
  const actions = doc?.founder_review?.top_3_improvements || [];
  const [done, setDone] = useState(() => {
    const out = {};
    for (let i = 0; i < actions.length; i++) {
      try {
        out[i] = localStorage.getItem(`aignoz.copilot.progress.${runId}.${i}`) === "1";
      } catch { out[i] = false; }
    }
    return out;
  });
  const toggle = (i) => {
    setDone((prev) => {
      const next = !prev[i];
      try { localStorage.setItem(`aignoz.copilot.progress.${runId}.${i}`, next ? "1" : "0"); } catch { /* ignore */ }
      return { ...prev, [i]: next };
    });
  };
  return (
    <SectionWrap
      kicker={
        <span className="inline-flex items-center gap-1.5">
          {labelOverride || "What should I do next?"}
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-white/30 cursor-help">ⓘ</span>
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-[240px]">
                From Aignoz Founder Review AI critique.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
      }
      testId="copilot-actions"
    >
      <ol className="space-y-2">
        {actions.length === 0 && <li className="text-xs text-white/40 italic">No actions yet.</li>}
        {actions.map((a, i) => (
          <li key={i} className="flex items-start gap-2.5" data-testid={`copilot-action-${i}`}>
            <button
              onClick={() => toggle(i)}
              data-testid={`copilot-action-check-${i}`}
              className={`shrink-0 w-4 h-4 rounded border mt-0.5 flex items-center justify-center transition ${
                done[i] ? "bg-teal-400/80 border-teal-400" : "border-white/20 hover:border-teal-400/50"
              }`}
              aria-label="Mark in progress"
            >
              {done[i] && <ListChecks className="w-2.5 h-2.5 text-slate-900" />}
            </button>
            <div className={`min-w-0 flex-1 text-[11px] leading-relaxed ${done[i] ? "text-white/40 line-through" : "text-white/85"}`}>
              <span className="text-teal-300/80 mono mr-1">{i + 1}.</span>{a}
            </div>
          </li>
        ))}
      </ol>
    </SectionWrap>
  );
}

function BlockersSection({ doc, labelOverride }) {
  const blockers = useMemo(() => computeTRLBlockers(doc), [doc]);
  return (
    <SectionWrap kicker={labelOverride || "What's blocking progression?"} testId="copilot-blockers">
      <div className="space-y-2">
        {blockers.length === 0 && <div className="text-xs text-white/40 italic">No blockers yet.</div>}
        {blockers.map((b, i) => (
          <div key={i} className="flex items-start gap-2" data-testid={`copilot-blocker-${i}`}>
            <AlertTriangle className="w-3.5 h-3.5 text-amber-300/80 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.14em] text-amber-200/70">{b.kind}</div>
              <div className="text-[11px] text-white/80 leading-snug mt-0.5">{b.text}</div>
            </div>
          </div>
        ))}
      </div>
    </SectionWrap>
  );
}

function RisksSection({ doc, runId, filter, labelOverride }) {
  const navigate = useNavigate();
  const risks = useMemo(() => topRisks(doc, 3, filter), [doc, filter]);
  return (
    <SectionWrap
      kicker={
        <div className="flex items-center justify-between">
          <span>{labelOverride || "Top risks"}</span>
          <button
            data-testid="copilot-risks-all"
            onClick={() => navigate(`/projects/${runId}/founder-review`)}
            className="text-[10px] text-teal-300/80 hover:text-teal-200 inline-flex items-center gap-0.5"
          >
            All <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      }
      testId="copilot-risks"
    >
      <div className="space-y-2">
        {risks.length === 0 && <div className="text-xs text-white/40 italic">No risks.</div>}
        {risks.map((r, i) => (
          <div key={i} className="space-y-0.5" data-testid={`copilot-risk-${i}`}>
            <div className="flex items-center gap-1.5">
              <span className={`text-[9px] px-1.5 py-0.5 rounded border uppercase tracking-wider ${sevColor(r.severity)}`}>
                {r.severity}
              </span>
              <span className="text-[11px] font-semibold text-white/85">{r.kind}</span>
            </div>
            <div className="text-[10px] text-white/55 leading-snug line-clamp-2">{r.explanation}</div>
          </div>
        ))}
      </div>
    </SectionWrap>
  );
}

function ReadinessSection({ doc, labelOverride, compact }) {
  const score = useMemo(() => computeFundingReadiness(doc), [doc]);
  return (
    <SectionWrap kicker={labelOverride || "Funding Readiness"} testId="copilot-readiness">
      <div className="flex items-end gap-3">
        <div className={`${compact ? "text-2xl" : "text-3xl"} font-semibold mono leading-none ${scoreColor(score)}`}>
          {score}
        </div>
        <div className="text-xs text-white/40 mono mb-1">/ 100</div>
        <div className="ml-auto flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
          <div className="h-full transition-all" style={{
            width: `${score}%`,
            background: score >= 70 ? "#5EEAD4" : score >= 50 ? "#FBBF24" : "#FB7185",
          }} />
        </div>
      </div>
      <div className="text-[10px] text-white/40 mt-2 leading-relaxed">
        Composite of investor attractiveness, feasibility, and commercialization risk.
      </div>
    </SectionWrap>
  );
}

function FundingSection({ doc }) {
  const f = useMemo(() => computeFundingRequired(doc), [doc]);
  const [open, setOpen] = useState(true);
  return (
    <SectionWrap kicker="Estimated funding required" testId="copilot-funding">
      <div className="flex items-baseline gap-2 flex-wrap">
        <div className="text-2xl font-semibold mono text-teal-200 leading-none" data-testid="copilot-funding-total-usd">{fmtUSD(f.total)}</div>
        <div className="text-base mono text-teal-100/85 leading-none" data-testid="copilot-funding-total-inr">· {formatINR(f.total)}</div>
        <div className="text-[10px] text-white/40">estimated</div>
        <button
          data-testid="copilot-funding-toggle"
          onClick={() => setOpen((o) => !o)}
          className="ml-auto text-[10px] text-teal-300/80 hover:text-teal-200"
        >
          {open ? "Hide" : "Show"} breakdown
        </button>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="break"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 space-y-1.5" data-testid="copilot-funding-breakdown">
              <Row label="Pilot hardware (100 units)" value={fmtUSD(f.breakdown.pilotHardware)} subValue={formatINR(f.breakdown.pilotHardware)} />
              <Row label="Validation" value={fmtUSD(f.breakdown.validation)} subValue={formatINR(f.breakdown.validation)} />
              <Row label={`Clinical trial (${f.sampleSize} pts · ${f.months}mo)`} value={fmtUSD(f.breakdown.trial)} subValue={formatINR(f.breakdown.trial)} />
              <Row label="Regulatory" value={fmtUSD(f.breakdown.regulatory)} subValue={formatINR(f.breakdown.regulatory)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="text-[10px] text-white/40 mt-2.5 leading-relaxed">
        Estimate based on prototype BoM, validation scope, and clinical trial design. <span className="text-white/55">Estimate, not a commitment.</span>
      </div>
    </SectionWrap>
  );
}

function Row({ label, value, subValue }) {
  return (
    <div className="flex items-center justify-between text-[11px] gap-2">
      <span className="text-white/55 truncate">{label}</span>
      <span className="mono ml-2 text-right leading-tight shrink-0">
        <span className="text-white/85">{value}</span>
        {subValue && <span className="block text-[10px] text-white/45">{subValue}</span>}
      </span>
    </div>
  );
}

function ComingSoonSection() {
  const modules = [
    "Grant Matching",
    "Incubator Matching",
    "Lab Matching",
    "Pilot Site Matching",
    "Investor Matching",
  ];
  return (
    <SectionWrap kicker={
      <span className="inline-flex items-center gap-1.5">
        Venture Progression Engine™
        <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border"
          style={{ color: "#D17EC0", borderColor: "rgba(209,126,192,0.4)", background: "rgba(209,126,192,0.06)" }}>
          Coming Soon
        </span>
      </span>
    } testId="copilot-coming">
      <div className="rounded-lg border bg-white/[0.015] p-3"
        style={{ borderColor: "rgba(209,126,192,0.18)" }}>
        <div className="text-xs text-white/55 mt-1 leading-relaxed">
          Live ecosystem matching is in development.
        </div>
        <ul className="mt-2.5 space-y-1.5">
          {modules.map((m) => (
            <li key={m} className="flex items-center gap-2 text-[11px] text-white/60" data-testid={`copilot-vpe-${m.toLowerCase().replace(/\s+/g, "-")}`}>
              <Lock className="w-3 h-3" style={{ color: "#D17EC0", opacity: 0.65 }} />
              {m}
            </li>
          ))}
        </ul>
        <Button
          size="sm"
          variant="outline"
          data-testid="copilot-vpe-waitlist"
          onClick={() => toast.success("Added to waitlist — we'll be in touch.")}
          className="mt-3 w-full h-8 text-xs"
          style={{ borderColor: "rgba(209,126,192,0.4)", color: "#F5C5E3" }}
        >
          Join Beta Waitlist
        </Button>
      </div>
    </SectionWrap>
  );
}

// ============================================================================
// Accordion wrapper — persistent open/close per section, persona-aware defaults
// ============================================================================
const ACCORDION_DEFAULTS = {
  founder: { blockers: false, risks: false, coming: false },
  investor: { blockers: false, risks: true, coming: false },
  researcher: { blockers: true, risks: false, coming: false },
  clinician: { blockers: true, risks: true, coming: false },
  incubator: { blockers: false, risks: false, coming: false },
  student: { blockers: false, risks: false, coming: false },
  enterprise: { blockers: false, risks: false, coming: false },
  guest: { blockers: false, risks: false, coming: false },
};

function Collapsible({ sectionKey, persona, header, testId, children }) {
  const storageKey = `aignoz.copilot.accordion.${sectionKey}`;
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === "1") return true;
      if (stored === "0") return false;
    } catch { /* ignore */ }
    return ACCORDION_DEFAULTS[persona]?.[sectionKey] ?? false;
  });
  // Re-sync to persona default if user hasn't manually set a state
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === null) {
        setOpen(ACCORDION_DEFAULTS[persona]?.[sectionKey] ?? false);
      }
    } catch { /* ignore */ }
  }, [persona, sectionKey, storageKey]);
  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  return (
    <div className="border-b border-white/[0.04]" data-testid={testId}>
      <button onClick={toggle}
        data-testid={`${testId}-toggle`}
        className="w-full flex items-center justify-between px-4 py-3 group hover:bg-white/[0.015] transition">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 group-hover:text-white/75 transition">
          {header}
        </div>
        <ChevronDown
          className={`w-3.5 h-3.5 text-white/40 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="pb-1 -mt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================================
// Main panel
// ============================================================================

export function FounderCopilot({ doc }) {
  const { id } = useParams();
  const { persona, config } = usePersona();
  const cfg = SECTION_CONFIG[persona] || SECTION_CONFIG.guest;
  const runId = id || doc?.run_id;

  // Last synced
  const synced = useMemo(() => {
    const t = doc?.completed_at || doc?.updated_at || doc?.created_at;
    if (!t) return null;
    const diff = Date.now() - new Date(t).getTime();
    const mins = Math.max(1, Math.floor(diff / 60000));
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }, [doc]);

  // Student auto-tip — show once per session
  useEffect(() => {
    if (persona !== "student") return;
    try {
      if (sessionStorage.getItem("aignoz.copilot.studentTip") === "1") return;
      sessionStorage.setItem("aignoz.copilot.studentTip", "1");
      setTimeout(() => toast.info("TRL is the DOD/NASA standard scale 1-9 for tech maturity.", { duration: 5500 }), 600);
    } catch { /* ignore */ }
  }, [persona]);

  const sectionMap = {
    trl: <TRLSection doc={doc} labelOverride={cfg.sectionLabels.trl} />,
    actions: <ActionsSection doc={doc} runId={runId} labelOverride={cfg.sectionLabels.actions} />,
    blockers: (
      <Collapsible sectionKey="blockers" persona={persona} testId="copilot-blockers-acc"
        header={cfg.sectionLabels.blockers || "What's blocking progression?"}>
        <BlockersSection doc={doc} labelOverride={null} />
      </Collapsible>
    ),
    risks: (
      <Collapsible sectionKey="risks" persona={persona} testId="copilot-risks-acc"
        header={cfg.sectionLabels.risks || "Top risks"}>
        <RisksSection doc={doc} runId={runId} filter={cfg.risksFilter} labelOverride={null} />
      </Collapsible>
    ),
    readiness: <ReadinessSection doc={doc} labelOverride={cfg.sectionLabels.readiness} compact={cfg.readinessCompact} />,
    funding: <FundingSection doc={doc} />,
    coming: (
      <Collapsible sectionKey="coming" persona={persona} testId="copilot-coming-acc"
        header={
          <span className="inline-flex items-center gap-1.5">
            Venture Progression Engine™
            <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
              style={{ color: "#D17EC0", border: "1px solid rgba(209,126,192,0.4)", background: "rgba(209,126,192,0.06)" }}>
              Coming Soon
            </span>
          </span>
        }>
        <ComingSoonSection />
      </Collapsible>
    ),
  };

  // Persona-specific section hiding
  let order = cfg.order;
  if (persona === "researcher") order = order.filter((k) => k !== "funding");
  if (persona === "clinician") order = order.filter((k) => k !== "readiness" && k !== "funding");

  const venture = doc?.final_report?.company_name || "Untitled Venture";

  return (
    <motion.div
      key={persona}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
      className="h-full flex flex-col" data-testid={`founder-copilot-${persona}`}
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-white/[0.04]" data-testid="copilot-header">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-teal-300/80" />
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: config.accent }}>
            {cfg.title}
          </div>
        </div>
        <div className="text-sm font-semibold tracking-tight mt-1 truncate">{venture}</div>
        <div className="flex items-center gap-2 mt-1.5">
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full border inline-flex items-center gap-1"
            style={{
              borderColor: `rgba(${config.accentRgb}, 0.40)`,
              background: `rgba(${config.accentRgb}, 0.08)`,
              color: config.accent,
            }}
          >
            <PersonaIcon name={config.icon} className="w-2.5 h-2.5" />
            Viewing as {config.name}
          </span>
          {synced && <span className="text-[10px] text-white/35 mono">Last synced {synced}</span>}
        </div>
        <div className="text-[10px] text-white/40 mt-1.5 italic">{cfg.tagline}</div>
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto" data-testid="copilot-body">
        {order.map((key) => (
          <div key={key}>{sectionMap[key]}</div>
        ))}

        {/* Footer */}
        <div className="px-4 py-3 text-center" data-testid="copilot-footer">
          <button
            data-testid="copilot-refresh"
            onClick={() => toast.info("Insights are computed from your current venture state.")}
            className="text-[10px] text-white/40 hover:text-teal-300 transition inline-flex items-center gap-1"
          >
            <RefreshCw className="w-2.5 h-2.5" />
            Refresh insights
          </button>
          <div className="text-[9px] uppercase tracking-[0.14em] text-white/25 mt-2">
            Powered by Aignoz Founder Review AI · 12-agent analysis
          </div>
        </div>
      </div>
    </motion.div>
  );
}
